import { node } from "prop-types";
import { useActiveProjectStore, useStores, useWorkspaceWebsocket } from "@hooks";
import { CoreCommands, ProjectCommands } from "@client";
import { getProjectDetailsQuery } from "@query";
import { PROJECT_COLUMNS, PROJECT_STATUS } from "@utils";
import { ActiveProjectsSocketContext } from "./ActiveProjectsSocketContext";

export const ActiveProjectsSocketProvider = ({ children }) => {
  
  const { userStore } = useStores();
  const activeProjects = useActiveProjectStore();

  const {
    socket,
    sendAction,
    requestProjectRename,
    socketInitialized,
    setupWorkspaceEvents,
  } = useWorkspaceWebsocket();
  
  const setupActiveProjectsEvents = (on) => {
    if(!on) {
      socket.current.removeAllListeners();
      setupWorkspaceEvents(true);
      return;
    }
    
    socket.current.on(ProjectCommands.USER_ADD, onProjectUserAdd);
    socket.current.on(ProjectCommands.USER_REMOVE, onProjectUserRemove);
    socket.current.on(ProjectCommands.USER_PROMOTE, onProjectUserPromote);
    // socket.current.on(CoreCommands.LOGOUT, onUserLogout);
    socket.current.on(ProjectCommands.PROJECT_CREATE, onProjectCreate);
    socket.current.on(ProjectCommands.PROJECT_RENAME, onProjectRename);
    socket.current.on(ProjectCommands.PROJECT_REMOVE, onProjectRemoveOrArchive);
    socket.current.on(ProjectCommands.PROJECT_ARCHIVE, onProjectRemoveOrArchive);
    socket.current.on(ProjectCommands.PROJECT_STATUS, onProjectStatusUpdate);
    socket.current.on(ProjectCommands.PROJECT_METADATA, onProjectMetadataUpdate);
    socket.current.on(CoreCommands.WORKTYPE_ADD, onAddNewWorkType);
    socket.current.on(CoreCommands.WORKTYPE_EDIT, onWorkTypeChange);
    socket.current.on(CoreCommands.WORKTYPE_REMOVE, onWorkTypeRemove);
  }
  
  const joinActiveProjects = () => {
    setupActiveProjectsEvents(true);
    socket.current.emit(CoreCommands.JOIN_WORKSPACE_PROJECTS, {
      companyId: userStore.companyId,
      workspaceUuid: userStore.workspaceUuid,
      userId: userStore.data.id,
      userUuid: userStore.data.uuid,
      name: userStore.data.fullname,
      email: userStore.data.email,
    });
  };
  const leaveActiveProjects = () => {
    setupActiveProjectsEvents(false);
    socket.current.emit(CoreCommands.LEAVE_WORKSPACE_PROJECTS);
  };
  
  const onAddNewWorkType = (wT) => {
    activeProjects.addCompanyWorkType(wT, true)
  };
  
  const onWorkTypeChange = ({ id, name, backgroundColor }) => {
    activeProjects.updateWorkTypeData(id, name, backgroundColor);
  };
  
  const onWorkTypeRemove = ({ id }) => {
    activeProjects.removeWorkType(id);
  };
  
  const onProjectRename = ({ projectUuid, newName }) => {
    activeProjects.editLocalProjectData(projectUuid, "name", newName);
  }
  
  const onProjectCreate = (projectData) => {
    activeProjects.addOrUpdateProject(projectData);
  }
  
  
  const onProjectRemoveOrArchive = ({ projectUuid }) => {
    activeProjects.editLocalProjectData(projectUuid, "remove");
  }
  
  const onProjectStatusUpdate = async ({ projectUuid, ...data }) => {
    if(data.sourceStatus === PROJECT_STATUS.ARCHIVE  && Object.values(PROJECT_COLUMNS).includes(data.status)) {
      const projectData = await getProjectDetailsQuery(projectUuid);
      activeProjects.addOrUpdateProject(projectData);
    } else
      activeProjects.editLocalProjectData(projectUuid, "status", data);
  }
  
  const onProjectMetadataUpdate = async ({ projectUuid, metadata }) => {
    if(!projectUuid || !metadata)
      return;
    activeProjects.editLocalProjectData(projectUuid, "metadata", metadata);
  }
  
  const onProjectUserPromote = ({ projectUuid, userUuid }) => {
    const p = activeProjects.projects.find(p => p.uuid === projectUuid);
    if(!p) return;
    const newOwner = p.users.observer.find((u) => u.uuid === userUuid);
    const observer = p.users.observer.filter((u) => u.uuid !== userUuid);
    if (p.users.owner && p.users.owner.uuid !== p.users.author.uuid)
      observer.push({ ...p.users.owner });
    activeProjects.editLocalProjectData(projectUuid, "owner", newOwner);
    activeProjects.editLocalProjectData(projectUuid, "observers", observer);
  };
  
  const onProjectUserAdd = ({ projectUuid, ...userData }) => {
    const p = activeProjects.projects.find(p => p.uuid === projectUuid);
    if(!p) return;
    activeProjects.editLocalProjectData(projectUuid, "observers", [...(p.users.observers || []), userData]);
  };
  
  const onProjectUserRemove = ({ projectUuid, userUuid }) => {
    const p = activeProjects.projects.find(p => p.uuid === projectUuid);
    if(!p) return;
      
    const observer = p.users.observer.filter((u) => u.uuid !== userUuid);
    if (p.users.owner && userUuid === p.users.owner.uuid) {
      const newOwner = p.users.author;
      observer.push({ ...p.users.owner });
      activeProjects.editLocalProjectData(projectUuid, "owner", newOwner);
    }
    
    activeProjects.editLocalProjectData(projectUuid, "observers", observer);
  };
  
  // --------- USER ACTIONS ---------
  
  const requestProjectUserAdd = (projectUuid, userData) => {
    sendAction(ProjectCommands.USER_ADD, { projectUuid, ...userData });
  };
  
  const requestProjectUserPromote = (projectUuid, userUuid) => {
    sendAction(ProjectCommands.USER_PROMOTE, { projectUuid, userUuid });
  };
  
  const requestProjectUserRemove = (projectUuid, userUuid) => {
    sendAction(ProjectCommands.USER_REMOVE, { projectUuid, userUuid });
  };
  
  const requestProjectArchive = (projectUuid) => {
    sendAction(ProjectCommands.PROJECT_ARCHIVE, { projectUuid });
  };
  
  const requestProjectRemove = (projectUuid) => {
    sendAction(ProjectCommands.PROJECT_REMOVE, { projectUuid });
  };
  
  const requestProjectDuplicate = (projectUuid, projectData) => {
    sendAction(ProjectCommands.PROJECT_CREATE, { projectUuid, projectData });
  };
  
  const requestProjectStatusChange = (projectUuid, status, order, sourceStatus, sourceOrder) => {
    sendAction(ProjectCommands.PROJECT_STATUS, { projectUuid, status, order, sourceStatus, sourceOrder });
  };
  
  const value = {
    socketInitialized,
    joinActiveProjects,
    leaveActiveProjects,
    
    requestProjectRename,
    requestProjectUserAdd,
    requestProjectUserPromote,
    requestProjectUserRemove,
    requestProjectArchive,
    requestProjectRemove,
    requestProjectDuplicate,
    requestProjectStatusChange,
  };

  return (
    <ActiveProjectsSocketContext.Provider value={value}>
      {children}
    </ActiveProjectsSocketContext.Provider>
  );
};

ActiveProjectsSocketProvider.propTypes = {
  children: node.isRequired,
};
